import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message, Spin } from "antd";
import { useRecoilValue } from "recoil";
import {
  ApiOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
  BarChartOutlined,
  CloudServerOutlined,
  CodeOutlined,
  DatabaseOutlined,
  FundOutlined,
  GitlabOutlined,
  LockOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import {
  tenantsInfo as recoilTenantsInfo,
  applicationPermissions as recoilApplicationPermissions,
} from "@/recoil";
import { envConfig } from "@/envConfig";
import { openInternalApp } from "@/utils/internalAppUrl";
import { getAccessToken, getRefreshToken } from "@/utils/sessionToken";
import { INTERNAL_APP_GROUP } from "./internal-apps/config";
import "./app-home.less";

const { homeUrl } = envConfig;

const REGION = process.env.REACT_APP_REGION;

const EXCHANGE_BASE =
  process.env.REACT_APP_NODE_ENV === "prod"
    ? "https://ldap2gasref.gs-robot.com/exchange"
    : "https://ldap2gasref.test.gs-robot.com/exchange";

type CategoryId = "service" | "factory" | "rd" | "ai" | "product";

interface AppItem {
  key: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  href: string;
  permission?: string;
  isTest?: boolean;
  isNew?: boolean;
  /** 需要通过 ldap2gasref exchange 换取 token 跳转 */
  useExchange?: boolean;
  exchangeTarget?: string;
}

interface CatalogApp extends AppItem {
  category: CategoryId;
  internalOnly?: boolean;
  navigationMode?: "default" | "internal";
}

interface Category {
  id: CategoryId;
  name: string;
  subtitle: string;
}

type PrototypeIconName =
  | "service"
  | "ota"
  | "factory"
  | "control"
  | "version"
  | "workbench"
  | "report"
  | "skill"
  | "model"
  | "clean"
  | "analysis"
  | "wechat"
  | "ticket"
  | "hotfix"
  | "api"
  | "review"
  | "iot"
  | "deploy"
  | "data"
  | "ai"
  | "avatar"
  | "prompt"
  | "benchmark"
  | "iam"
  | "center";

const createPrototypeIcon = (children: React.ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const PROTOTYPE_APP_ICONS: Record<PrototypeIconName, React.ReactNode> = {
  service: createPrototypeIcon(
    <>
      <path d="M14.5 5.5a4 4 0 0 0-5 5L4 16l4 4 5.5-5.5a4 4 0 0 0 5-5l-3 3-4-4 3-3Z" />
      <path d="m6.5 17.5 2-2" />
    </>
  ),
  ota: createPrototypeIcon(
    <>
      <path d="M12 3v11m0-11L8 7m4-4 4 4" />
      <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </>
  ),
  factory: createPrototypeIcon(
    <>
      <path d="M3 21V9l6 3V8l6 4V5h6v16H3Z" />
      <path d="M7 16h2m4 0h2m4 0h2" />
    </>
  ),
  control: createPrototypeIcon(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3m0 14v3M2 12h3m14 0h3M5 5l2 2m10 10 2 2m0-14-2 2M7 17l-2 2" />
    </>
  ),
  version: createPrototypeIcon(
    <>
      <path d="M6 3v12m0 0-3-3m3 3 3-3M18 21V9m0 0-3 3m3-3 3 3" />
      <path d="M9 5h4a5 5 0 0 1 5 4M15 19h-4a5 5 0 0 1-5-4" />
    </>
  ),
  workbench: createPrototypeIcon(
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v3m0 14v3M2 12h3m14 0h3M5 5l2 2m10 10 2 2m0-14-2 2M7 17l-2 2" />
    </>
  ),
  report: createPrototypeIcon(
    <>
      <path d="M4 20V10m5 10V4m6 16v-7m5 7V7M2 20h20" />
    </>
  ),
  skill: createPrototypeIcon(
    <>
      <path d="M12 3 5 7v5c0 4.6 2.8 7.4 7 9 4.2-1.6 7-4.4 7-9V7l-7-4Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  model: createPrototypeIcon(
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  clean: createPrototypeIcon(
    <>
      <path d="M12 3c3 4 6 7 6 11a6 6 0 0 1-12 0c0-4 3-7 6-11Z" />
      <path d="M9 15c.6 1.3 1.6 2 3 2" />
    </>
  ),
  analysis: createPrototypeIcon(
    <>
      <path d="M4 18V9m5 9V5m5 13v-6m5 6V8" />
      <path d="m4 7 5-4 5 7 5-4" />
    </>
  ),
  wechat: createPrototypeIcon(
    <>
      <path d="M4 5h16v11H9l-5 4V5Z" />
      <path d="M8 9h8m-8 3h5" />
    </>
  ),
  ticket: createPrototypeIcon(
    <>
      <path d="M5 3h14v18l-3-2-4 2-4-2-3 2V3Z" />
      <path d="M8 8h8m-8 4h8m-8 4h5" />
    </>
  ),
  hotfix: createPrototypeIcon(<path d="m13 2-8 12h7l-1 8 8-12h-7l1-8Z" />),
  api: createPrototypeIcon(
    <>
      <path d="m8 5-5 7 5 7m8-14 5 7-5 7M14 3l-4 18" />
    </>
  ),
  review: createPrototypeIcon(
    <>
      <path d="M4 4h16v16H4z" />
      <path d="m8 12 2.5 2.5L16 9" />
    </>
  ),
  iot: createPrototypeIcon(
    <>
      <rect x="7" y="7" width="10" height="10" rx="2" />
      <path d="M9 2v3m6-3v3M9 19v3m6-3v3M2 9h3m-3 6h3m14-6h3m-3 6h3" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
  deploy: createPrototypeIcon(
    <>
      <path d="M12 15V3m0 0L8 7m4-4 4 4M5 13v7h14v-7" />
    </>
  ),
  data: createPrototypeIcon(
    <>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v7c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12v7c0 1.7 3.6 3 8 3s8-1.3 8-3v-7" />
    </>
  ),
  ai: createPrototypeIcon(
    <path d="m12 2 1.4 5.6L19 9l-5.6 1.4L12 16l-1.4-5.6L5 9l5.6-1.4L12 2Zm7 13 .7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7L19 15Z" />
  ),
  avatar: createPrototypeIcon(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  prompt: createPrototypeIcon(
    <>
      <path d="M4 4h16v13H9l-5 4V4Z" />
      <path d="m9 8-2 2 2 2m4 0h4" />
    </>
  ),
  benchmark: createPrototypeIcon(
    <path d="M5 21V9m5 12V4m5 17v-8m5 8V7M3 21h19" />
  ),
  iam: createPrototypeIcon(
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19a6 6 0 0 1 12 0M17 11v6m-3-3h6" />
    </>
  ),
  center: createPrototypeIcon(<path d="M4 4h16v16H4zM4 9h16M9 9v11" />),
};

const PROTOTYPE_ICON_BY_APP_KEY: Record<string, PrototypeIconName> = {
  operationMaintenance: "service",
  otaManagement: "ota",
  factoryManagement: "factory",
  parameterFlowManagement: "control",
  firmwareDevelopmentPlatform: "version",
  workbench: "workbench",
  reportCentre: "report",
  skillHub: "skill",
  modelHub: "model",
  taskImages: "clean",
  robotTaskAnalysis: "analysis",
  wecomMessageMonitor: "wechat",
  afterSalesTicketAnalysis: "ticket",
  factoryHotfix: "hotfix",
  apiPlatform: "api",
  codeReview: "review",
  iot: "iot",
  internalDeploymentCenter: "deploy",
  basicDataManage: "data",
  gaussianAiWorkbench: "ai",
  digitalTwinCenter: "avatar",
  internalKnowledgeAssistant: "ai",
  gsPromptUpdate: "prompt",
  wikiBenchmark: "benchmark",
  iamManage: "iam",
  dataCenterManage: "center",
};

const CATEGORIES: Category[] = [
  {
    id: "service",
    name: "售后运维",
    subtitle: "售后服务、远程运维与机器人运营人员",
  },
  {
    id: "factory",
    name: "工厂管理",
    subtitle: "工厂管理、设备维护、测试与制造人员",
  },
  {
    id: "rd",
    name: "研发效能",
    subtitle: "软件研发、测试、项目交付与研发管理人员",
  },
  {
    id: "ai",
    name: "AI基座",
    subtitle: "算法、AI 应用研发、知识库运营与内部 AI 用户",
  },
  {
    id: "product",
    name: "产品管理",
    subtitle: "产品、权限与数据平台管理人员",
  },
];

const STANDARD_APPS: CatalogApp[] = [
  {
    key: "parameterFlowManagement",
    title: "机器分析与管控",
    description: "机器人任务管理、告警事件管理与部署管理等",
    icon: <SettingOutlined />,
    href: `${homeUrl}/parameter-flow-management`,
    permission: "PTM",
    category: "rd",
  },
  {
    key: "otaManagement",
    title: "OTA升级管理",
    description: "机器人升级与固件版本管理平台",
    icon: <CloudServerOutlined />,
    href: `${homeUrl}/ota-management`,
    permission: "UpgradeManagement",
    category: "service",
  },
  {
    key: "operationMaintenance",
    title: "售后运维系统",
    description:
      "机器人问题诊断、远程运维、智能客服、工单运营等面向售后业务的一体化平台",
    icon: <ToolOutlined />,
    href: `${homeUrl}/aop`,
    permission: "AOP",
    category: "service",
  },
  {
    key: "firmwareDevelopmentPlatform",
    title: "版本研发平台",
    description: "分支管理、高级参数与国际化词条等版本研发能力",
    icon: <CodeOutlined />,
    href: `${homeUrl}/firmware-development-platform`,
    permission: "FirmwareDevelopmentPlatform",
    category: "rd",
  },
  {
    key: "skillHub",
    title: "Skill Hub",
    description: "Skill技能统一管理平台",
    icon: <RocketOutlined />,
    href: "https://skillhub.gs-robot.com",
    isNew: true,
    useExchange: true,
    exchangeTarget: "skillhub.gs-robot.com",
    category: "rd",
  },
  {
    key: "modelHub",
    title: "Model Hub",
    description: "Model模型统一管理平台",
    icon: <AppstoreOutlined />,
    href: "https://modelhub.gs-robot.com",
    isNew: true,
    useExchange: true,
    exchangeTarget: "modelhub.gs-robot.com",
    category: "rd",
  },
  {
    key: "apiPlatform",
    title: "接口平台",
    description: "yapi 接口文档与调试平台",
    icon: <ApiOutlined />,
    href: "https://grod.gs-robot.com",
    isNew: true,
    useExchange: true,
    exchangeTarget: "grod.gs-robot.com",
    category: "rd",
    internalOnly: true,
  },
  {
    key: "codeReview",
    title: "CodeReview",
    description: "代码审查与研发协作平台",
    icon: <GitlabOutlined />,
    href: "http://8.153.90.1/",
    isTest: true,
    category: "rd",
  },
  {
    key: "factoryManagement",
    title: "工厂信息管理",
    description: "工厂跑测、设备档案等信息管理平台，仙智造 APP 下载",
    icon: <DatabaseOutlined />,
    href: `${homeUrl}/factory-management`,
    permission: "FactoryInfoManagement",
    category: "factory",
  },
  {
    key: "iamManage",
    title: "IAM权限管理",
    description: "租户、账号、角色与应用权限统一管理",
    icon: <SafetyCertificateOutlined />,
    href: `${homeUrl}/tenant-center#/gas/tenant/manage`,
    category: "product",
  },
  {
    key: "dataCenterManage",
    title: "数据中心管理",
    description: "数据中心后台配置与报表门户资源管理",
    icon: <FundOutlined />,
    href: `${homeUrl}/partner-report`,
    category: "product",
  },
  {
    key: "iot",
    title: "物联网平台",
    description: "设备接入、物联数据与连接能力管理",
    icon: <CloudServerOutlined />,
    href: `${homeUrl}/iot`,
    category: "rd",
  },
  {
    key: "basicDataManage",
    title: "基础数据管理",
    description: "业务基础数据配置与维护",
    icon: <DatabaseOutlined />,
    href: `${homeUrl}/beep`,
    category: "rd",
  },
];

const INTERNAL_APP_CATEGORY: Record<string, CategoryId> = {
  digitalTwinCenter: "ai",
  taskImages: "service",
  gsPromptUpdate: "ai",
  internalKnowledgeAssistant: "ai",
  factoryHotfix: "factory",
  robotTaskAnalysis: "service",
  internalDeploymentCenter: "rd",
  wecomMessageMonitor: "service",
  afterSalesTicketAnalysis: "service",
  gaussianAiWorkbench: "ai",
  wikiBenchmark: "ai",
};

const INTERNAL_APP_DISPLAY: Record<
  string,
  { title: string; description: string }
> = {
  taskImages: {
    title: "清洁效果标注",
    description: "查看机器人清洁效果截图、标记有无水渍",
  },
  robotTaskAnalysis: {
    title: "机器人任务分析",
    description: "机器人任务数据分析与问题排查工具",
  },
  wecomMessageMonitor: {
    title: "企业微信消息监控",
    description: "企业微信业务消息监控工具",
  },
  afterSalesTicketAnalysis: {
    title: "售后工单分析中心",
    description: "售后工单智能分析与故障诊断中枢",
  },
  factoryHotfix: {
    title: "工厂参数热修",
    description: "工厂设备维护人员参数热修工具",
  },
  internalDeploymentCenter: {
    title: "内网应用部署中心",
    description: "申请服务器资源 和部署内网应用于发布",
  },
  gaussianAiWorkbench: {
    title: "Gaussian AI工作台",
    description: "Gaussian AI 内部工作台与应用协作空间",
  },
  digitalTwinCenter: {
    title: "高仙数字分身中心",
    description: "数字分身、Workspace、Skills 与 Sessions 管理平台",
  },
  internalKnowledgeAssistant: {
    title: "高仙内AI知识库助手",
    description: "高仙内部 AI 知识查询与问答助手",
  },
  gsPromptUpdate: {
    title: "GS提示词更新",
    description: "提示词查找、复用与维护平台",
  },
  wikiBenchmark: {
    title: "Wiki benchmark测试平台",
    description: "Gaussian Wiki 与内部应用知识库测评平台",
  },
};

const INTERNAL_APPS: CatalogApp[] = [
  ...INTERNAL_APP_GROUP.featuredItems,
  ...INTERNAL_APP_GROUP.otherItems,
]
  .filter((item) => INTERNAL_APP_CATEGORY[item.key])
  .map((item) => ({
    ...item,
    ...INTERNAL_APP_DISPLAY[item.key],
    category: INTERNAL_APP_CATEGORY[item.key],
    internalOnly: true,
    navigationMode: "internal",
  }));

const ALL_CATALOG_APPS = [...STANDARD_APPS, ...INTERNAL_APPS];

const findCatalogApp = (key: string): CatalogApp => {
  const app = ALL_CATALOG_APPS.find((item) => item.key === key);
  if (!app) {
    throw new Error(`Missing application config: ${key}`);
  }
  return app;
};

const GENERAL_TOOLS: CatalogApp[] = [
  {
    key: "workbench",
    title: "工作台",
    description: "机器人故障分析、远程控制、高级参数与日志记录",
    icon: <SettingOutlined />,
    href: `${homeUrl}/parameter-flow-management/#/workbench`,
    permission: "PTM",
    category: "rd",
  },
  {
    key: "reportCentre",
    title: "报表中心",
    description: "多指标维度数据看板与经营报表",
    icon: <BarChartOutlined />,
    href: `${homeUrl}/report-centre`,
    permission: "ReportCentre",
    category: "product",
  },
  findCatalogApp("skillHub"),
  findCatalogApp("modelHub"),
];

const APPS_BY_CATEGORY: Record<CategoryId, CatalogApp[]> = {
  service: [
    findCatalogApp("operationMaintenance"),
    findCatalogApp("otaManagement"),
    findCatalogApp("taskImages"),
    findCatalogApp("robotTaskAnalysis"),
    findCatalogApp("wecomMessageMonitor"),
    findCatalogApp("afterSalesTicketAnalysis"),
  ],
  factory: [
    findCatalogApp("factoryManagement"),
    findCatalogApp("factoryHotfix"),
  ],
  rd: [
    findCatalogApp("parameterFlowManagement"),
    findCatalogApp("firmwareDevelopmentPlatform"),
    findCatalogApp("apiPlatform"),
    findCatalogApp("codeReview"),
    findCatalogApp("iot"),
    findCatalogApp("internalDeploymentCenter"),
    findCatalogApp("basicDataManage"),
  ],
  ai: [
    findCatalogApp("gaussianAiWorkbench"),
    findCatalogApp("digitalTwinCenter"),
    findCatalogApp("internalKnowledgeAssistant"),
    findCatalogApp("gsPromptUpdate"),
    findCatalogApp("wikiBenchmark"),
  ],
  product: [findCatalogApp("iamManage"), findCatalogApp("dataCenterManage")],
};

const CATALOG_APPS = CATEGORIES.flatMap(
  (category) => APPS_BY_CATEGORY[category.id]
);

const HOT_APP_KEYS = [
  "operationMaintenance",
  "otaManagement",
  "parameterFlowManagement",
  "firmwareDevelopmentPlatform",
  "factoryManagement",
];

const REGION_APP_KEYS = [
  "parameterFlowManagement",
  "otaManagement",
  "operationMaintenance",
];

const APPHome: React.FC = () => {
  const tenantsInfo = useRecoilValue(recoilTenantsInfo);
  const applicationPermissions = useRecoilValue(recoilApplicationPermissions);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<"all" | CategoryId>(
    "all"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tenantsInfo?.internalPerson !== undefined) {
      setLoading(false);
    }
  }, [tenantsInfo?.internalPerson]);

  const openSearch = useCallback(() => {
    setIsSearchExpanded(true);
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);

  useEffect(() => {
    const handleSearchShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openSearch();
      }
    };

    window.addEventListener("keydown", handleSearchShortcut);
    return () => window.removeEventListener("keydown", handleSearchShortcut);
  }, [openSearch]);

  const exchangeAndRedirect = async (_target: string, fallbackHref: string) => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      message.error("登录已过期，请重新登录");
      return;
    }

    const newWindow = window.open("about:blank", "_blank");

    setLoading(true);
    try {
      const exchangeUrl = `${EXCHANGE_BASE}?token=${encodeURIComponent(
        refreshToken
      )}&default_return_to=${encodeURIComponent(fallbackHref)}`;
      await fetch(exchangeUrl, { mode: "no-cors", credentials: "include" });
      if (newWindow) {
        newWindow.location.href = fallbackHref;
      }
    } catch {
      if (newWindow) {
        newWindow.close();
      }
      message.error("跳转失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  const canAccess = (permission?: string) =>
    !permission || applicationPermissions?.includes(permission);

  const handleAppClick = (item: CatalogApp) => {
    if (item.navigationMode === "internal") {
      const accessToken = getAccessToken();
      if (!accessToken) {
        message.error("登录已过期，请重新登录");
        return;
      }
      openInternalApp(item.href, accessToken);
      return;
    }

    if (!canAccess(item.permission)) {
      message.error("权限未开通，如需访问请联系管理员");
      return;
    }
    if (item.useExchange && item.exchangeTarget) {
      exchangeAndRedirect(item.exchangeTarget, item.href);
      return;
    }
    window.open(item.href, "_blank", "noopener,noreferrer");
  };

  const hotApps = useMemo(
    () =>
      HOT_APP_KEYS.map((key) =>
        CATALOG_APPS.find((item) => item.key === key)
      ).filter((item): item is CatalogApp => Boolean(item)),
    []
  );

  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
  const visibleCategoryGroups = useMemo(() => {
    const categories =
      activeCategory === "all"
        ? CATEGORIES
        : CATEGORIES.filter((category) => category.id === activeCategory);

    return categories
      .map((category) => ({
        category,
        apps: APPS_BY_CATEGORY[category.id].filter((item) => {
          if (!normalizedSearchQuery) {
            return true;
          }
          return `${item.title} ${item.description ?? ""}`
            .toLocaleLowerCase()
            .includes(normalizedSearchQuery);
        }),
      }))
      .filter(({ apps }) => apps.length > 0);
  }, [activeCategory, normalizedSearchQuery]);

  const renderAppCard = (
    item: CatalogApp,
    variant: "default" | "compact" = "default"
  ) => {
    const locked = !canAccess(item.permission);

    return (
      <button
        key={item.key}
        type="button"
        className={`portal-card portal-card--${
          item.category
        } portal-card--${variant}${locked ? " portal-card--locked" : ""}`}
        data-app-key={item.key}
        onClick={() => handleAppClick(item)}
        aria-label={`${item.title}${
          locked ? "，暂无访问权限" : "，在新标签页打开"
        }`}
        aria-disabled={locked}
      >
        <span className="portal-card-icon" aria-hidden="true">
          {PROTOTYPE_APP_ICONS[PROTOTYPE_ICON_BY_APP_KEY[item.key]] ??
            item.icon}
        </span>
        <span className="portal-card-body">
          <span className="portal-card-title">
            <span className="portal-card-title-text">{item.title}</span>
            {(item.isNew || item.isTest || item.internalOnly) && (
              <span className="portal-card-status">
                {item.isNew && <span className="portal-new-tag">NEW</span>}
                {item.isTest && <span className="portal-test-tag">内测</span>}
                {item.internalOnly && (
                  <span className="portal-internal-tag">仅限内网</span>
                )}
              </span>
            )}
          </span>
          <span className="portal-card-desc">{item.description}</span>
        </span>
        <span className="portal-card-open" aria-hidden="true">
          {locked ? <LockOutlined /> : <ArrowRightOutlined />}
        </span>
      </button>
    );
  };

  if (REGION) {
    const regionApps = REGION_APP_KEYS.map((key) =>
      STANDARD_APPS.find((item) => item.key === key)
    ).filter((item): item is CatalogApp => Boolean(item));

    return (
      <div className="portal-root portal-root--simple">
        <Spin spinning={loading} wrapperClassName="portal-spin">
          <div className="portal-shell">
            <section className="portal-hero portal-hero--compact">
              <div className="portal-hero-copy">
                <div className="portal-hero-title-row">
                  <h1>运维运营工作台</h1>
                </div>
                <p>集中访问设备管控、升级管理与线上运维入口。</p>
              </div>
            </section>
            <div className="portal-simple-grid">
              {regionApps.map((item) => renderAppCard(item))}
            </div>
          </div>
        </Spin>
      </div>
    );
  }

  return (
    <div className="portal-root">
      <Spin spinning={loading} wrapperClassName="portal-spin">
        <div className="portal-shell">
          <section className="portal-hero">
            <div className="portal-hero-copy">
              <div className="portal-hero-title-row">
                <h1>高仙内部应用</h1>
                <div
                  className={`portal-search-control${
                    isSearchExpanded ? " is-expanded" : ""
                  }`}
                  role="search"
                  onBlur={(event) => {
                    if (
                      !event.currentTarget.contains(event.relatedTarget) &&
                      !searchQuery
                    ) {
                      setIsSearchExpanded(false);
                    }
                  }}
                >
                  <button
                    type="button"
                    className="portal-search-trigger"
                    aria-label="搜索应用"
                    aria-expanded={isSearchExpanded}
                    onClick={() => {
                      if (isSearchExpanded && !searchQuery) {
                        setIsSearchExpanded(false);
                        return;
                      }
                      openSearch();
                    }}
                  >
                    <SearchOutlined />
                  </button>
                  <input
                    ref={searchInputRef}
                    type="search"
                    value={searchQuery}
                    placeholder="搜索应用名称或功能"
                    autoComplete="off"
                    tabIndex={isSearchExpanded ? 0 : -1}
                    aria-label="搜索应用名称或功能"
                    onChange={(event) => {
                      setSearchQuery(event.target.value);
                      setActiveCategory("all");
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        setSearchQuery("");
                        setActiveCategory("all");
                        setIsSearchExpanded(false);
                        searchInputRef.current?.blur();
                      }
                    }}
                  />
                  <kbd>⌘ K</kbd>
                </div>
              </div>
              <p>按使用角色与工作场景重新归类，快速识别访问要求。</p>
              <div className="portal-notice">
                <span className="portal-notice-icon" aria-hidden="true">
                  i
                </span>
                <span>涉及公司内部信息，请勿随意分享页面或截图。</span>
              </div>
            </div>
          </section>

          <div className="portal-top-panels">
            <section className="portal-hot-section" aria-labelledby="hot-title">
              <div className="portal-section-heading">
                <div className="portal-section-title-wrap">
                  <h2 id="hot-title">热门应用</h2>
                </div>
              </div>
              <div className="portal-hot-grid">
                {hotApps.map((item) => renderAppCard(item, "compact"))}
              </div>
            </section>

            <aside
              className="portal-utility-rail"
              aria-labelledby="utility-title"
            >
              <div className="portal-section-heading">
                <div className="portal-section-title-wrap">
                  <h2 id="utility-title">通用工具</h2>
                </div>
              </div>
              <div className="portal-utility-grid">
                {GENERAL_TOOLS.map((item) => renderAppCard(item, "compact"))}
              </div>
            </aside>
          </div>

          <nav className="portal-category-tabs" aria-label="应用分类">
            <span className="portal-category-label">
              <AppstoreOutlined aria-hidden="true" />
              应用分类
            </span>
            <button
              type="button"
              className={`portal-category-tab${
                activeCategory === "all" ? " is-active" : ""
              }`}
              aria-pressed={activeCategory === "all"}
              onClick={() => setActiveCategory("all")}
            >
              全部
            </button>
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`portal-category-tab${
                  activeCategory === category.id ? " is-active" : ""
                }`}
                aria-pressed={activeCategory === category.id}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </nav>

          <main className="portal-catalog">
            {visibleCategoryGroups.length > 0 ? (
              visibleCategoryGroups.map(({ category, apps }) => (
                <section
                  key={category.id}
                  className={`portal-category-section portal-category-section--${category.id}`}
                  aria-labelledby={`category-${category.id}`}
                >
                  <div className="portal-section-heading portal-category-heading">
                    <div className="portal-section-title-wrap">
                      <span
                        className="portal-section-mark"
                        aria-hidden="true"
                      />
                      <h2 id={`category-${category.id}`}>{category.name}</h2>
                    </div>
                  </div>
                  <div className="portal-card-grid">
                    {apps.map((item) => renderAppCard(item))}
                  </div>
                </section>
              ))
            ) : (
              <section className="portal-empty-state" aria-live="polite">
                <SearchOutlined aria-hidden="true" />
                <h2>未找到相关应用</h2>
                <p>请尝试更换关键词或查看全部分类</p>
              </section>
            )}
          </main>
        </div>
      </Spin>
    </div>
  );
};

export default APPHome;
